import { Component } from "@kluntje/core";
import { getParent, addClass, removeClass, hasClass } from "@kluntje/js-utils/lib/dom-helpers";

class FRAFormLegacy extends Component {
  constructor() {
    super({
      ui: {
        radios: "[type=radio]",
        checkBoxes: "[type=checkbox]",
        selects: "select",
        inputs: "[type=text], [type=email], [type=date]",
        dateInputs: "[type=date]",
        allInputs: "input",
        buttons: "button",
      },
      events: [
        {
          event: "focus",
          target: "inputs",
          handler: "handleFocus",
        },
        {
          event: "blur",
          target: "inputs",
          handler: "handleBlur",
        },
        {
          event: "click",
          target: "buttons",
          handler: "updateUi",
        },
      ],
    });
  }

  /**
   * Css classes for the inputs
   */
  private readonly INPUT_WRAPPER = "fra-m-form-legacy__input-wrapper";
  private readonly INPUT_LEGACY = "fra-m-form-legacy__input";
  private readonly INPUT_LABEL = "fra-m-form-legacy__input-label";
  private readonly INPUT_WRAPPER_TEXT = "fra-m-form-legacy__input-wrapper--text";
  private readonly INPUT_WRAPPER_RADIO = "fra-m-form-legacy__input-wrapper--radio";
  private readonly INPUT_WRAPPER_CHECKBOX = "fra-m-form-legacy__input-wrapper--checkbox";
  private readonly INPUT_FLOATED_LABEL = "fra-m-form-legacy__input--has-floated-label";
  private readonly INPUT_SELECT = "fra-m-form-legacy__select-wrapper";
  private readonly BUTTON = "fra-e-button";

  /**
   * @override
   * Add css classes after the component rendering
   */
  afterComponentRender(): void {
    this.addCustomStylingClasses();
  }

  /**
   * Update the UI and add css classes.
   * Usefull when other formelements are added to the dom.
   */
  updateUi() {
    this.updateUI();
    this.updateEvents();
    this.addCustomStylingClasses();
  }

  /**
   * Add css classes to an input group with a structure like "label > input"
   * and wrap the label text in a span tag.
   * @param {Array} inputs - array of inputs element
   */
  handleInputsClasses(inputs: HTMLInputElement[]): void {
    inputs.forEach(input => {
      const label = getParent(input, "label") as HTMLLabelElement;
      if (label && !hasClass(label, this.INPUT_WRAPPER)) {
        addClass(input, this.INPUT_LEGACY);
        addClass(label, this.INPUT_WRAPPER);
        const spanEl = document.createElement("span");
        const labelText = label.innerText;
        label.childNodes.forEach(node => {
          if (typeof node.nodeValue === "string") {
            node.nodeValue = "";
          }
        });
        spanEl.innerText = labelText;
        addClass(spanEl, this.INPUT_LABEL);
        input.after(spanEl);

        if (input.getAttribute("type") === "date") {
          const iconEl = document.createElement("fra-e-icon");
          addClass(iconEl, "fra-e-icon");
          iconEl.setAttribute("icon-id", "fra-icon-kalender");
          spanEl.after(iconEl);
        }
      }
    });
  }

  /**
   * Add css classes to a label with a structure like "label > input"
   * from a given array of inputs
   * @param {Array} inputs
   * @param {string} cssClass
   */
  addClassToLabel(inputs: HTMLInputElement[], cssClass: string) {
    inputs.forEach(input => {
      const label = getParent(input, "label");
      if (label && !hasClass(label, cssClass)) addClass(label, cssClass);
    });
  }

  /**
   * Wrap a select with a div and add the INPUT_SELECT class
   * @param {Array} selects
   */
  handleSelectsClasses(selects: HTMLSelectElement[]) {
    selects.forEach(select => {
      const parentEl = select.parentElement;
      if (parentEl && !hasClass(parentEl, this.INPUT_SELECT)) {
        const wrapperEl = document.createElement("div");
        parentEl.prepend(wrapperEl);
        addClass(wrapperEl, this.INPUT_SELECT);
        wrapperEl.prepend(select);
      }
    });
  }

  /**
   * Add the BUTTON css class to all buttons
   * @param {Array} buttons
   */
  handleButtonsClasses(buttons: HTMLButtonElement[]) {
    buttons.forEach(button => {
      if (button && !hasClass(button, this.BUTTON)) {
        addClass(button, this.BUTTON);
      }
    });
  }

  /**
   * Give the styling classes to all form elements
   */
  addCustomStylingClasses() {
    const { radios, checkBoxes, selects, inputs, allInputs, buttons } = this.ui;
    this.handleInputsClasses(allInputs);
    this.addClassToLabel(radios, this.INPUT_WRAPPER_RADIO);
    this.addClassToLabel(checkBoxes, this.INPUT_WRAPPER_CHECKBOX);
    this.addClassToLabel(inputs, this.INPUT_WRAPPER_TEXT);
    this.handleButtonsClasses(buttons);
    this.handleSelectsClasses(selects);
  }

  /**
   * Add the INPUT_FLOATED_LABEL css class when a given input is focused
   * @param {Event} event
   */
  handleFocus(event: Event): void {
    addClass(event.target as HTMLInputElement, this.INPUT_FLOATED_LABEL);
  }

  /**
   * Check if an input has content or not add or remove the INPUT_FLOATED_LABEL css class
   * @param {Event} event
   */
  handleBlur(event: Event): void {
    if ((event.target as HTMLInputElement).value.trim() === "") {
      removeClass(event.target as HTMLInputElement, this.INPUT_FLOATED_LABEL);
    } else {
      addClass(event.target as HTMLInputElement, this.INPUT_FLOATED_LABEL);
    }
  }
}

customElements.define("fra-m-form-legacy", FRAFormLegacy);

export default FRAFormLegacy;
